export type PolarPoint = {
  id: string;
  tws: number;
  twa: number;
  speed: number;
};

export type PolarExportFormat = 'certified-json' | 'table-semicolon' | 'table-space';

export type PolarDocument =
  | {
      format: 'empty';
      points: PolarPoint[];
      valid: true;
      message: '';
    }
  | {
      format: 'certified-json';
      points: PolarPoint[];
      valid: boolean;
      message: string;
      payload: Record<string, unknown>;
      metricIndex: number;
    }
  | {
      format: 'table';
      points: PolarPoint[];
      valid: boolean;
      message: string;
      separator: ';' | 'space';
    };

const CERTIFIED_JSON_FORMAT = 'certified-json';
const BOAT_SPEED_METRICS = new Set(['boatspeed', 'bsp', 'speed']);

const normalizeMetric = (value: unknown): string =>
  String(value ?? '')
    .toLowerCase()
    .split(/\s+/)
    .join('');

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toFiniteNumber = (value: unknown): number | null => {
  const normalized = String(value ?? '').trim().replace(',', '.');
  if (normalized.length === 0) {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const toPositiveSpeedNumber = (value: unknown): number | null => {
  const parsed = toFiniteNumber(value);
  return parsed !== null && parsed > 0 ? parsed : null;
};

const makePointId = (tws: number, twa: number, index: number): string =>
  `${tws.toString()}-${twa.toString()}-${index.toString()}`;

const sortPoints = (points: PolarPoint[]): PolarPoint[] =>
  [...points].sort((left, right) => left.twa - right.twa || left.tws - right.tws);

export const parsePolarDocument = (raw: string): PolarDocument => {
  const value = raw.trim();
  if (value.length === 0) {
    return { format: 'empty', points: [], valid: true, message: '' };
  }

  if (value.startsWith('{')) {
    try {
      const payload = JSON.parse(value) as unknown;
      if (!isRecord(payload) || !Array.isArray(payload.polars)) {
        return {
          format: CERTIFIED_JSON_FORMAT,
          points: [],
          valid: false,
          message: 'JSON must include polars[]',
          payload: isRecord(payload) ? payload : {},
          metricIndex: -1,
        };
      }

      const metricIndex = payload.polars.findIndex((item) => {
        if (!isRecord(item)) {
          return false;
        }
        return BOAT_SPEED_METRICS.has(normalizeMetric(item.metric));
      });
      const metric = metricIndex >= 0 ? payload.polars[metricIndex] : null;
      const polarData = isRecord(metric) ? metric.polarData : null;
      if (!Array.isArray(polarData)) {
        return {
          format: CERTIFIED_JSON_FORMAT,
          points: [],
          valid: false,
          message: 'JSON must include BoatSpeed polarData',
          payload,
          metricIndex,
        };
      }

      const points = polarData
        .map((item, index) => {
          if (!isRecord(item)) {
            return null;
          }
          const tws = toFiniteNumber(item.tws);
          const twa = toFiniteNumber(item.twa);
          const speed = toPositiveSpeedNumber(item.metricValue);
          return tws !== null && twa !== null && speed !== null
            ? { id: makePointId(tws, twa, index), tws, twa, speed }
            : null;
        })
        .filter((point): point is PolarPoint => point !== null);

      return {
        format: CERTIFIED_JSON_FORMAT,
        points: sortPoints(points),
        valid: points.length > 0,
        message: points.length > 0 ? '' : 'JSON polarData has no valid BoatSpeed points',
        payload,
        metricIndex,
      };
    } catch {
      return {
        format: CERTIFIED_JSON_FORMAT,
        points: [],
        valid: false,
        message: 'Invalid JSON polar format',
        payload: {},
        metricIndex: -1,
      };
    }
  }

  const rows = value
    .split(/\r?\n/)
    .map((row) => row.trim())
    .filter(Boolean);
  const separator: ';' | 'space' = rows.some((row) => row.includes(';')) ? ';' : 'space';
  const splitRow = (row: string): string[] =>
    separator === ';' ? row.split(';').map((cell) => cell.trim()) : row.split(/\s+/);

  if (rows.length < 2) {
    return { format: 'table', points: [], valid: false, message: 'Table needs a header and data row', separator };
  }

  const header = splitRow(rows[0]);
  const twsValues = header.slice(1).map((value) => toFiniteNumber(value));
  if (twsValues.length === 0 || twsValues.includes(null)) {
    return { format: 'table', points: [], valid: false, message: 'TWS header values must be numeric', separator };
  }

  const points: PolarPoint[] = [];
  for (const [rowIndex, row] of rows.slice(1).entries()) {
    const cells = splitRow(row);
    if (cells.length !== header.length) {
      return { format: 'table', points, valid: false, message: 'Rows must keep the same number of columns', separator };
    }
    const twa = toFiniteNumber(cells[0]);
    if (twa === null) {
      return { format: 'table', points, valid: false, message: 'TWA values must be numeric', separator };
    }
    for (const [columnIndex, rawSpeed] of cells.slice(1).entries()) {
      const speedToken = rawSpeed.split('±')[0].trim();
      if (speedToken.length === 0) {
        continue;
      }
      const speed = toFiniteNumber(speedToken);
      if (speed === null || speed < 0) {
        return { format: 'table', points, valid: false, message: 'BoatSpeed values must be numeric', separator };
      }
      const tws = twsValues[columnIndex];
      if (speed > 0 && tws !== null) {
        points.push({ id: makePointId(tws, twa, rowIndex * twsValues.length + columnIndex), tws, twa, speed });
      }
    }
  }

  return {
    format: 'table',
    points: sortPoints(points),
    valid: points.length > 0,
    message: points.length > 0 ? '' : 'Table has no valid speed values',
    separator,
  };
};

const formatNumber = (value: number): string => {
  if (!Number.isFinite(value)) {
    return '';
  }
  return Number.parseFloat(value.toFixed(4)).toString();
};

export const serializePolarDocument = (document: PolarDocument, points: PolarPoint[]): string => {
  const sorted = sortPoints(points);
  if (document.format === CERTIFIED_JSON_FORMAT) {
    const payload = structuredClone(document.payload) as Record<string, unknown>;
    const polars = Array.isArray(payload.polars) ? [...payload.polars] : [];
    const metricIndex =
      document.metricIndex >= 0
        ? document.metricIndex
        : polars.findIndex((item) => isRecord(item) && BOAT_SPEED_METRICS.has(normalizeMetric(item.metric)));
    const nextMetric: Record<string, unknown> = isRecord(polars[metricIndex])
      ? { ...polars[metricIndex] }
      : { metric: 'BoatSpeed' };
    nextMetric.polarData = sorted.map((point) => ({
      tws: point.tws,
      twa: point.twa,
      metricValue: point.speed,
    }));
    if (metricIndex >= 0) {
      polars[metricIndex] = nextMetric;
    } else {
      polars.push(nextMetric);
    }
    payload.polars = polars;
    if (!Array.isArray(payload.targets)) {
      payload.targets = [];
    }
    return JSON.stringify(payload, null, 2);
  }

  const separator = document.format === 'table' && document.separator === 'space' ? ' ' : ';';
  const twsValues = [...new Set(sorted.map((point) => point.tws))].sort((a, b) => a - b);
  const twaValues = [...new Set(sorted.map((point) => point.twa))].sort((a, b) => a - b);
  const lines = [`TWA${separator}${twsValues.map((value) => formatNumber(value)).join(separator)}`];
  for (const twa of twaValues) {
    const cells = twsValues.map(
      (tws) => sorted.find((point) => point.twa === twa && point.tws === tws)?.speed,
    );
    lines.push([formatNumber(twa), ...cells.map((speed) => (speed === undefined ? '' : formatNumber(speed)))].join(separator));
  }
  return lines.join('\n');
};

export const getPolarDocumentFormat = (document: PolarDocument): PolarExportFormat | 'empty' | 'invalid-json' => {
  if (document.format === 'empty') {
    return 'empty';
  }
  if (document.format === CERTIFIED_JSON_FORMAT) {
    return document.valid ? CERTIFIED_JSON_FORMAT : 'invalid-json';
  }
  return document.separator === 'space' ? 'table-space' : 'table-semicolon';
};

export const convertPolarDocument = (
  document: PolarDocument,
  targetFormat: PolarExportFormat,
  points: PolarPoint[] = document.points,
): string => {
  const sorted = sortPoints(points);
  if (targetFormat === CERTIFIED_JSON_FORMAT) {
    const sourceDocument: PolarDocument =
      document.format === CERTIFIED_JSON_FORMAT
        ? document
        : {
            format: CERTIFIED_JSON_FORMAT,
            points: sorted,
            valid: sorted.length > 0,
            message: sorted.length > 0 ? '' : 'No points to convert',
            payload: {
              polars: [{ metric: 'BoatSpeed', polarData: [] }],
              targets: [],
            },
            metricIndex: 0,
          };
    return serializePolarDocument(sourceDocument, sorted);
  }

  const tableDocument: PolarDocument = {
    format: 'table',
    points: sorted,
    valid: sorted.length > 0,
    message: sorted.length > 0 ? '' : 'No points to convert',
    separator: targetFormat === 'table-space' ? 'space' : ';',
  };
  return serializePolarDocument(tableDocument, sorted);
};

export const summarizePolarPoints = (points: PolarPoint[]) => ({
  pointCount: points.length,
  twsCount: new Set(points.map((point) => point.tws)).size,
  twaCount: new Set(points.map((point) => point.twa)).size,
});
